import { AppPageWrapper } from "@/components/layout/app-page-wrapper";
import { useRole } from "@/hooks/use-role";
import { useFreelancerProfile } from "@/lib/hooks/use-freelancer-profile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StepIndicator } from "@/components/ui/step-indicator";
import { StatusBadge } from "@/components/ui/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  Clock,
  Users,
  Calendar,
  ArrowRight,
  BookOpen,
  UserCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";
import type { Step } from "@/components/ui/step-indicator";

export default function FreelancerReviewStatus() {
  const { freelancerProfileId } = useRole();
  const {
    isLoading,
    reviewStatus,
    submittedForReviewAt,
  } = useFreelancerProfile(freelancerProfileId, { enableMutations: false });

  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AppPageWrapper>
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-48 w-full rounded-3xl" />
          <Skeleton className="h-64 w-full rounded-3xl" />
        </div>
      </AppPageWrapper>
    );
  }

  // Determine current step based on review status
  const getSteps = (): Step[] => {
    const isSubmitted = reviewStatus === "submitted";
    const isApproved = reviewStatus === "approved";

    return [
      {
        id: "submitted",
        label: "Profile Submitted",
        description: submittedForReviewAt
          ? `Submitted ${new Date(submittedForReviewAt).toLocaleDateString()}`
          : "Your profile has been submitted",
        status: "completed",
      },
      {
        id: "verification",
        label: "Expert Verification",
        description: isApproved
          ? "Verification complete"
          : isSubmitted
          ? "Currently being reviewed"
          : "Pending",
        status: isApproved ? "completed" : isSubmitted ? "current" : "pending",
      },
      {
        id: "interview",
        label: "Interview",
        description: isApproved ? "Interview completed" : "30-45 min video call",
        status: isApproved ? "completed" : "pending",
      },
      {
        id: "matching",
        label: "Start Matching",
        description: isApproved ? "You're now receiving opportunities" : "Get matched with projects",
        status: isApproved ? "completed" : "pending",
      },
    ];
  };

  const whileYouWaitActions = [
    {
      id: "enhance",
      icon: UserCircle,
      title: "Enhance Your Profile",
      description: "Add more details to stand out",
      href: "/genius/profile",
      color: "bg-blue-100 text-blue-600",
    },
    {
      id: "learn",
      icon: BookOpen,
      title: "Continue Learning",
      description: "Explore advanced courses",
      href: "/genius/courses",
      color: "bg-amber-100 text-amber-600",
    },
    {
      id: "prepare",
      icon: Calendar,
      title: "Prepare for Interview",
      description: "Review common questions",
      href: "#",
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      id: "community",
      icon: Users,
      title: "Join Community",
      description: "Connect with other experts",
      href: "#",
      color: "bg-purple-100 text-purple-600",
    },
  ];

  const faqs = [
    {
      id: "timeline",
      question: "How long does the review process take?",
      answer:
        "Typically, the initial review takes 2-3 business days. If selected for an interview, you'll receive scheduling options within 5 business days.",
    },
    {
      id: "criteria",
      question: "What are the approval criteria?",
      answer:
        "We evaluate your expertise, experience, portfolio quality, and training completion. We look for demonstrated skills and professional presentation.",
    },
    {
      id: "changes",
      question: "Can I update my profile during review?",
      answer:
        "Yes! You can continue to enhance your profile while under review. Updates may be considered in the evaluation process.",
    },
    {
      id: "rejected",
      question: "What happens if my profile is not approved?",
      answer:
        "You'll receive feedback on areas for improvement. After making updates, you can resubmit your profile for another review.",
    },
  ];

  return (
    <AppPageWrapper>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Status Card */}
        <Card className="card-warning rounded-3xl overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-amber-100">
                  <Clock className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-[hsl(var(--color-7))]">
                    Your Profile is Under Review
                  </h1>
                  <p className="text-[hsl(var(--color-8))] mt-1">
                    We're reviewing your profile to ensure the best experience for our clients.
                  </p>
                </div>
              </div>
              <StatusBadge status="review" size="lg" />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4 bg-white/50 rounded-2xl">
                <div className="text-2xl font-bold text-[hsl(var(--color-4))]">
                  100%
                </div>
                <div className="text-sm text-[hsl(var(--color-8))]">
                  Profile Complete
                </div>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-2xl">
                <div className="text-2xl font-bold text-[hsl(var(--color-4))]">
                  4/4
                </div>
                <div className="text-sm text-[hsl(var(--color-8))]">
                  Training Courses
                </div>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-2xl">
                <div className="text-2xl font-bold text-[hsl(var(--color-4))]">
                  2-3
                </div>
                <div className="text-sm text-[hsl(var(--color-8))]">
                  Business Days
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="rounded-3xl border-[hsl(var(--color-3))]">
          <CardHeader>
            <CardTitle className="text-xl">Review Process Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <StepIndicator steps={getSteps()} orientation="vertical" size="lg" />
          </CardContent>
        </Card>

        {/* While You Wait */}
        <Card className="rounded-3xl border-[hsl(var(--color-3))]">
          <CardHeader>
            <CardTitle className="text-xl">While You Wait</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {whileYouWaitActions.map((action) => (
                <Link key={action.id} to={action.href}>
                  <div className="action-card group flex items-start gap-4">
                    <div
                      className={`p-3 rounded-xl ${action.color} transition-transform group-hover:scale-110`}
                    >
                      <action.icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[hsl(var(--color-7))] group-hover:text-[hsl(var(--color-4))]">
                        {action.title}
                      </h3>
                      <p className="text-sm text-[hsl(var(--color-8))]">
                        {action.description}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-[hsl(var(--color-8))] group-hover:text-[hsl(var(--color-4))] transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="rounded-3xl border-[hsl(var(--color-3))]">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-[hsl(var(--color-4))]" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="border border-[hsl(var(--color-3))] rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[hsl(var(--color-2))] transition-colors"
                  onClick={() =>
                    setExpandedFaq(expandedFaq === faq.id ? null : faq.id)
                  }
                >
                  <span className="font-medium text-[hsl(var(--color-7))]">
                    {faq.question}
                  </span>
                  {expandedFaq === faq.id ? (
                    <ChevronUp className="h-5 w-5 text-[hsl(var(--color-8))]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[hsl(var(--color-8))]" />
                  )}
                </button>
                {expandedFaq === faq.id && (
                  <div className="px-4 pb-4 text-sm text-[hsl(var(--color-8))] animate-slide-in">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Back to Dashboard */}
        <div className="text-center">
          <Link to="/genius/dashboard">
            <Button variant="outline" size="lg" className="rounded-full">
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </AppPageWrapper>
  );
}
